import React from "react";
import { Box, ButtonBase, Paper, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import IconMask from "../IconMask";

const getRowIcon = (row, iconConfig, fallbackIcon) => {
  const iconName = iconConfig?.path
    ? row?.[iconConfig.path]?.[iconConfig.field]
    : null;
  return iconName ? `/assets/icons/${iconName}` : fallbackIcon;
};

export default function TableGridView({
  getLabel,
  icon,
  iconConfig,
  isSelected,
  model,
  onOpen,
  onSelect,
  rows,
}) {
  const theme = useTheme();

  return (
    <Box
      data-testid="table-grid-view"
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
          lg: "repeat(4, minmax(0, 1fr))",
          xl: "repeat(6, minmax(0, 1fr))",
        },
        p: 2,
      }}
    >
      {rows.map((row, index) => {
        const selected = isSelected(row);
        const rowIcon = getRowIcon(row, iconConfig, icon);
        const accessibleName = row.vc_nombre || model?.title || "Elemento";

        return (
          <Paper
            data-testid={`table-grid-card-${row.id || index}`}
            elevation={selected ? 4 : 1}
            key={row.id || index}
            sx={{
              border: "1px solid",
              borderColor: selected
                ? "primary.main"
                : "rgba(160, 170, 180, 0.35)",
              borderRadius: 1,
              minHeight: 176,
              overflow: "hidden",
              transition: theme.transitions.create(
                ["background-color", "border-color", "box-shadow", "transform"],
                { duration: theme.transitions.duration.shorter }
              ),
              ...(selected && {
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
              }),
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                borderColor: alpha(theme.palette.primary.main, 0.7),
                boxShadow: theme.shadows[4],
                transform: "translateY(-2px)",
              },
              "&:focus-within": {
                borderColor: "primary.main",
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.28)}`,
              },
              "&:hover .table-grid-icon": {
                transform: "scale(1.06)",
              },
            }}
          >
            <ButtonBase
              aria-label={`Seleccionar ${accessibleName}`}
              aria-pressed={selected}
              onClick={(event) => onSelect(event, row)}
              onDoubleClick={(event) => {
                event.stopPropagation();
                onOpen(row);
              }}
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "center",
                p: 2,
                width: "100%",
                "&.Mui-focusVisible": {
                  outline: "none",
                },
              }}
            >
              <Box
                className="table-grid-icon"
                sx={{
                  alignItems: "center",
                  border: "1px solid",
                  borderColor: selected
                    ? "primary.main"
                    : "rgba(160, 170, 180, 0.45)",
                  borderRadius: "50%",
                  display: "flex",
                  height: 80,
                  justifyContent: "center",
                  mb: 1.5,
                  transition: theme.transitions.create(
                    ["border-color", "transform"],
                    { duration: theme.transitions.duration.shorter }
                  ),
                  width: 80,
                }}
              >
                <IconMask
                  color={selected ? theme.palette.primary.main : "grey"}
                  url={rowIcon}
                  size={48}
                />
              </Box>

              {model?.focus?.length ? (
                model.focus.map((focus, focusIndex) => (
                  <Typography
                    alignSelf="center"
                    fontWeight={focus.fontWeight || undefined}
                    key={focus.id || focus.path || focusIndex}
                    sx={{
                      lineHeight: 1.25,
                      overflowWrap: "anywhere",
                      textAlign: "center",
                      width: "100%",
                    }}
                    variant={focus.variant}
                  >
                    {getLabel(row[focus.path], focus)}
                  </Typography>
                ))
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      lineHeight: 1.25,
                      overflowWrap: "anywhere",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    {row.vc_nombre}
                  </Typography>
                  {!!row.tipoActivo?.vc_nombre && (
                    <Typography
                      color="text.secondary"
                      sx={{
                        fontSize: "0.8rem",
                        lineHeight: 1.25,
                        mt: 0.5,
                        overflowWrap: "anywhere",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {row.tipoActivo.vc_nombre}
                    </Typography>
                  )}
                </>
              )}
            </ButtonBase>
          </Paper>
        );
      })}
    </Box>
  );
}
